import { useAtomValue } from 'jotai';
import { MyInfoState } from '@/recoil/atom';
import { fetchLeaveHistory, useGetVacationStatus } from '@/hooks/apiHooks';
import process from 'process';
import { t } from 'i18next';
import { useEffect, useMemo, useState } from 'react';
import { LeaveCancelType, VacationStatus } from '@/types/api';
import { getApprovalDocUrl, getIntranetDocUrl } from '@/utils/urls';
import openCancelApprovalCreate from '@/utils/approval/openCancelApprovalCreate';
import { AxiosError } from 'axios';
import { fetchApi } from '@/utils/api/customAxios';
import { useDialog, useSnackbar } from '@/hooks/global-tools';
import {
  durationToMinutes,
  formatLeaveDays,
  leaveGrantedMinutes,
  leaveUsedMinutes,
  vacationRemainingMinutes,
} from '@/utils/leave';
import { AnnualLeaveModalCompanyCode } from '@/constant';

/** 휴가사용 상세내역 행 종류: 사용 / 전년 초과 사용 / 부여(발급) */
type LeaveRowType = 'usage' | 'overage' | 'granted';

type TableData = {
  rowType: LeaveRowType;
  hasLink: boolean;
  date: string;
  title: string;
  detail: string;
  documentId?: string;
  // 사용(usage) 행의 취소 가능 유형 (immediate/approval/none)
  cancelType?: LeaveCancelType;
  onClick?: () => void;
};

/** 연차 취소 신청 Approval 양식 apv_type */
const ANNUAL_LEAVE_CANCEL_APV_TYPE = 232;

const currYear = new Date().getFullYear();
const useVacationProps = () => {
  const { passportId, joinedAt, companyCode } = useAtomValue(MyInfoState);
  const dialog = useDialog();
  const { showSuccess } = useSnackbar();

  // 법인코드에 따라 신규 '연차 신청 모달' 사용 여부 분기 (PORTAL-11903)
  const useAnnualLeaveModal = AnnualLeaveModalCompanyCode.includes(companyCode);

  const joinedYear = useMemo(() => Number(joinedAt.split('-')[0]), [joinedAt]);

  const yearOptions = useMemo(() => {
    function generateYearArray(startYear: number): number[] {
      const currentYear = new Date().getFullYear();
      if (startYear > currentYear) {
        throw new Error('시작 연도는 현재 연도보다 클 수 없습니다.');
      }

      const years: number[] = [];
      for (let year = startYear; year <= currentYear; year++) {
        years.unshift(year);
      }

      return years;
    }

    try {
      return generateYearArray(joinedYear).map((item) => ({
        label: `${item}년`,
        value: `${item}`,
      }));
    } catch (error) {
      return [{ label: `${currYear}년`, value: `${currYear}` }];
    }
  }, [joinedYear]);

  const [prevYear, setPrevYear] = useState(currYear);
  const [prevYearTableData, setPrevYearTableData] = useState<TableData[]>([]);

  const {
    data: statusData,
    isLoading: isStatusDataLoading,
    error: statusError,
    mutate: mutateStatus,
  } = useGetVacationStatus(passportId);

  const {
    data: prevYearData,
    isLoading: isPrevYearDataLoading,
    error: prevYearError,
    mutate: mutatePrevYear,
  } = fetchLeaveHistory({
    passportId,
    year: prevYear,
  });

  // 휴가 현황(잔여) + 상세내역 동시 갱신. 신청/취소 성공 후 호출.
  const refetchVacation = () => Promise.all([mutateStatus(), mutatePrevYear()]);

  // immediate: 결재 없이 즉시 취소 (DELETE) → 휴가 현황/상세내역 갱신
  const cancelLeave = async (documentId: string) => {
    try {
      await fetchApi({
        method: 'DELETE',
        url: `/api/v1/passport/annual-leave/${documentId}`,
      });
      await refetchVacation();
      showSuccess({ message: '취소가 완료되었습니다.' });
    } catch (e) {
      const status = e instanceof AxiosError ? e.response?.status : null;
      dialog.show({
        type: 'alert',
        message:
          status === 422
            ? '이미 처리된 신청건은 취소가 불가능합니다.'
            : '취소 처리 중 오류가 발생했습니다.',
      });
    }
  };

  // approval: 취소 신청 품의(apv_type=232) 화면으로 새 탭 이동 (V2/레거시 분기는 헬퍼)
  const moveToCancelApproval = (documentId: string) =>
    openCancelApprovalCreate(ANNUAL_LEAVE_CANCEL_APV_TYPE, documentId);

  const sendPostMessage = () => {
    const sendData = JSON.stringify({
      actionType: 'MOVE_TAB',
      tabName: 'approval',
      url: `${process.env.NEXT_PUBLIC_IFRAME_URL}/intranet/approval/create?apv_type=32`,
    });
    window?.ReactNativeWebView?.postMessage(sendData);
  };

  const sendDetailPostMessage = (documentId: string) => {
    const sendData = JSON.stringify({
      actionType: 'MOVE_TAB',
      tabName: 'approval',
      url: getIntranetDocUrl(documentId, false),
    });
    window?.ReactNativeWebView?.postMessage(sendData);
  };

  const handleOpen = () => {
    if (window?.isRNWebview) {
      sendPostMessage();
    } else {
      window.open(
        `${process.env.NEXT_PUBLIC_IFRAME_URL}/intranet/approval/create?apv_type=32`,
        '_blank',
        'noreferrer',
      );
    }
  };

  const handleDetail = (documentId?: string) => {
    if (window?.isRNWebview) {
      documentId && sendDetailPostMessage(documentId);
    } else {
      documentId &&
        window.open(
          getApprovalDocUrl(documentId, false),
          '_blank',
          'noreferrer',
        );
    }
  };

  const status = useMemo(() => {
    let status = isStatusDataLoading ? 'loading' : 'success';

    if (statusError) {
      if (statusError as AxiosError) {
        const e = statusError as AxiosError;
        const data = e.response?.data as any;
        if (data && data.code === '0404') {
          return 'empty';
        }
      }

      status = 'error';
    }

    return status;
    // 의존성이 비어 있으면 첫 렌더 값(loading)으로 굳어, 캐시 없이 바로 연 화면(앱 휴가 현황 조회)이 스켈레톤에 남았다 (PORTAL-15332)
  }, [isStatusDataLoading, statusError]);

  useEffect(() => {
    if (prevYearError || status === 'empty') {
      setPrevYearTableData([]);
      return;
    }

    if (prevYearData) {
      const conversionDate = (str: string) => {
        const date = new Date(str);
        return `${('00' + (date.getMonth() + 1).toString()).slice(-2)}월 ${(
          '00' + date.getDate().toString()
        ).slice(-2)}일`;
      };

      const {
        leaves = [],
        overages,
        monthlyLeaveGranted,
        annualLeaveGranted,
      } = prevYearData;

      const result: TableData[] = leaves.map((item) => ({
        rowType: 'usage',
        onClick: () => handleDetail(item.documentId),
        hasLink: Boolean(item.documentId),
        documentId: item.documentId,
        cancelType: item.cancelType,

        date:
          item.startDate === item.endDate
            ? conversionDate(item.startDate)
            : `${conversionDate(item.startDate)} ~ ${conversionDate(
                item.endDate,
              )}`,
        title: t('MYPAGE.사용'),
        detail: `-${formatLeaveDays(item.days)}`,
      }));

      const startDate =
        joinedYear === prevYear ? conversionDate(joinedAt) : '01월 01일';

      if (overages > 0) {
        result.push({
          rowType: 'overage',
          hasLink: false,
          date: startDate,
          title: t('MYPAGE.전년 초과 사용'),
          detail: `-${formatLeaveDays(overages)}`,
        });
      }

      if (annualLeaveGranted > 0) {
        result.push({
          rowType: 'granted',
          hasLink: false,
          date: t('MYPAGE.연차 부여'),
          title: t('MYPAGE.발급'),
          detail: `+${formatLeaveDays(annualLeaveGranted)}`,
        });
      }

      if (monthlyLeaveGranted > 0) {
        result.push({
          rowType: 'granted',
          hasLink: false,
          date: t('MYPAGE.월차 부여'),
          title: t('MYPAGE.발급'),
          detail: `+${formatLeaveDays(monthlyLeaveGranted)}`,
        });
      }

      setPrevYearTableData(result);
    }
  }, [prevYearData, joinedYear, joinedAt, prevYear, prevYearError, status]);

  return {
    status: {
      status,
      data: getStatusData(statusData),
    },
    applyButtonOnClick: handleOpen,
    useAnnualLeaveModal,
    cancelLeave,
    moveToCancelApproval,
    refetchVacation,
    prevYear,
    prevYearData: {
      status:
        status === 'empty'
          ? 'success'
          : isPrevYearDataLoading
          ? 'loading'
          : prevYearError
          ? 'error'
          : 'success',
      data: prevYearTableData,
    },
    yearOptions,
    yearOnChange: (e: any) => {
      setPrevYear(Number(e.target.value));
    },
  };
};

export default useVacationProps;

const getStatusData = (data?: VacationStatus) => {
  if (!data) {
    return {
      remainingMinutes: 0,
      overageMinutes: 0,
    };
  }

  const { monthlyLeave, annualLeave, overage = 0, overageDuration } = data;

  const monthly = {
    grantedMinutes: leaveGrantedMinutes(monthlyLeave),
    usedMinutes: leaveUsedMinutes(monthlyLeave),
  };
  const annual = {
    grantedMinutes: leaveGrantedMinutes(annualLeave),
    usedMinutes: leaveUsedMinutes(annualLeave),
  };
  const overageMinutes = durationToMinutes(
    overageDuration ?? { day: overage, hour: 0, minute: 0 },
  );

  return {
    monthly,
    annual,
    remainingMinutes: vacationRemainingMinutes(data),
    overageMinutes,
  };
};
